import "./receptionTypes.js";
import React, { memo, useEffect, useState } from "react";
import { alpha, Box, Button, Tooltip, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarPlus,
  faPersonWalking,
} from "@fortawesome/free-solid-svg-icons";
import * as api from "../../services/api.js";
import * as f from "../../utils/formatter.js";
import Link from "../Link.jsx";
import { useCurrentLocation } from "../LocationSelector.jsx";
import { createClickHandler } from "../calendar.jsx";
import { createUrlString } from "../../url.js";
import IconLanguage from "../icons/IconLanguage.jsx";
import IconAngry from "../icons/IconAngry.jsx";
import { RESERVATION_MODES } from "../reservationDialog/ReservationDialog.jsx";
import { faLinkHorizontal } from "@fortawesome/pro-solid-svg-icons";
import { getForeignSpeakingOnlyTitle } from "shared/src/client.mjs";
import { useClientProblem } from "../client/hooks/useClientProblem.jsx";
import { useRoles } from "../../context/AuthenticationTokenContext.jsx";

/**
 * A component to display a client that has been treated
 *
 * @typedef {object} BookedClientProps
 * @property {Appointment} appointment
 * @property {function} setReservationDialogOptions Set the options for the reservation dialog
 * @property {string} hoverOverId
 * @property {function} setHoverOverId Setter function for the hoverOverId state
 * @property {number} order_from_h Number of hours in advance a follow-up appointment can be booked
 *
 * @param {BookedClientProps} props
 * @returns {JSX.Element}
 */
const TreatedClient = memo(
  ({
    appointment,
    setReservationDialogOptions,
    hoverOverId,
    setHoverOverId,
    order_from_h,
  }) => {
    const location_id = useCurrentLocation();
    const theme = useTheme();
    const roles = useRoles();
    const problem = useClientProblem(appointment.client, roles);
    const languages = getForeignSpeakingOnlyTitle(appointment.client);

    // State for the latest effective receptionist note (with merged latest version) and flag for service presence
    const [latestNote, setLatestNote] = useState(null);
    const [hasReceptionReservation, setHasReceptionReservation] =
      useState(null);

    function pickLatestReceptionVersion(notes) {
      if (!Array.isArray(notes)) return null;
      const recNotes = notes.filter((n) => n?.note_type === "receptionist");
      if (!recNotes.length) return null;

      const pickLatestOf = (versions = []) => {
        if (!Array.isArray(versions) || versions.length === 0) return null;
        const explicit = versions.find((v) => v?.is_latest);
        if (explicit) return explicit;
        return versions.slice().sort((a, b) => {
          const ta = Date.parse(a?.edited_at || 0) || 0;
          const tb = Date.parse(b?.edited_at || 0) || 0;
          if (tb !== ta) return tb - ta;
          const va = a?.version_number ?? -Infinity;
          const vb = b?.version_number ?? -Infinity;
          return vb - va;
        })[0];
      };

      const candidates = recNotes.map((n) => {
        const v = pickLatestOf(n.receptionist_versions) || null;
        const merged = v ? { ...n, ...v } : n;
        const ts =
          Date.parse(
            merged?.edited_at || merged?.updated_at || merged?.created_at || 0,
          ) || 0;
        return { ...merged, _ts: ts };
      });

      return candidates.sort((a, b) => b._ts - a._ts)[0] || null;
    }

    useEffect(() => {
      let mounted = true;
      (async () => {
        const res = await api.fetchAppointmentNotes(appointment.id);
        const notes = res?.data?.appointmentNotes ?? [];
        const latest = pickLatestReceptionVersion(notes);
        if (mounted) setLatestNote(latest);
        setHasReceptionReservation(isActionable(latest));
      })();
      return () => {
        mounted = false;
      };
    }, [appointment.id]);

    const handleMouseEnter = (id) => {
      if (id) setHoverOverId(id);
    };

    const handleMouseLeave = () => setHoverOverId("");

    const clickHandler = createClickHandler({
      onClick: () => {},
      onDoubleClick: () => {
        const clientId = appointment.client?.id;
        if (!clientId) return;
        window.open(
          createUrlString(
            location.origin,
            location.search,
            `/clients/${clientId}`,
          ),
          "_blank",
        );
      },
    });

    const isActionable = (v) => {
      if (!v || !v.service_id) return false;
      if (v.reservation_unit === "earliestAvailableDate") return true;
      const count = Number(v.reservation_count);
      return !!v.reservation_unit && Number.isFinite(count) && count > 0;
    };

    return (
      <Box
        sx={{
          position: "relative",
          borderBottom: "1px solid lightblue",
          padding: "12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          gap: "5px",
          backgroundColor:
            appointment.group_id === hoverOverId ||
            appointment.reservation_id === hoverOverId
              ? alpha(theme.palette.primary.main, 0.2)
              : "background.paper",
        }}
        onClick={clickHandler}
        onMouseEnter={() =>
          handleMouseEnter(appointment.group_id ?? appointment.reservation_id)
        }
        onMouseLeave={handleMouseLeave}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            flex: "0 0 90%",
            minWidth: "0",
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <Link
                to={`/clients/${appointment.client.id}`}
                style={{
                  color: "inherit",
                  textDecoration: "none",
                  fontWeight: "bold",
                  fontSize: "14px",
                  display: "flex",
                  gap: "0.5rem",
                  alignItems: "center",
                  minWidth: "0",
                }}
              >
                <Box
                  sx={{
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {f.fullName(appointment.client)}
                </Box>
              </Link>
              {languages != null && (
                <Tooltip title={languages}>
                  <IconLanguage />
                </Tooltip>
              )}
              {problem != null && (
                <Tooltip title={problem}>
                  <IconAngry />
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>

        {hasReceptionReservation != null &&
          (hasReceptionReservation ? (
            <Box sx={{ flex: "0 0 10%", textAlign: "right" }}>
              <Button
                onClick={() => {
                  (async function setReservationOptions() {
                    const [locRes] = await Promise.all([api.locationOptions()]);
                    const locations = locRes?.data?.locations?.data ?? [];
                    const currentLocation =
                      locations.find((l) => l?.id === location_id) ??
                      locations[0] ??
                      null;

                    let service = null;
                    if (latestNote?.service_id) {
                      try {
                        const srvRes = await api.fetchServiceDetail({
                          id: latestNote.service_id,
                          location_id,
                        });
                        service = srvRes?.data?.service ?? null;
                      } catch {
                        service = null;
                      }
                    }

                    setReservationDialogOptions({
                      close: () => setReservationDialogOptions(null),
                      defaults: {
                        client: appointment.client,
                        locationOption: currentLocation,
                        serviceOption: service
                          ? { ...service, ...service.locationService }
                          : null,
                        oldAppointment: appointment,
                        mode: RESERVATION_MODES.FOLLOWUP,
                        note: latestNote,
                        order_from_h: order_from_h,
                      },
                    });
                  })();
                }}
                variant="contained"
                color="primary"
                sx={{
                  display: "inline-flex",
                  borderRadius: 1,
                  color: "white",
                  width: "2.6rem",
                  height: "2.6rem",
                  fontSize: "1.8rem",
                  minWidth: 0,
                  px: 0,
                }}
              >
                <FontAwesomeIcon icon={faCalendarPlus} />
              </Button>
            </Box>
          ) : (
            <Box sx={{ flex: "0 0 10%", textAlign: "right" }}>
              <FontAwesomeIcon
                color="#b2c6de"
                style={{ fontSize: "1.8rem", padding: "0.25rem" }}
                icon={faPersonWalking}
              />
            </Box>
          ))}

        {appointment.group_id === hoverOverId && (
          <Box
            component="span"
            sx={{
              position: "absolute",
              left: "50%",
              bottom: "5px",
              color: "primary.dark",
            }}
          >
            <FontAwesomeIcon icon={faLinkHorizontal} />
          </Box>
        )}
      </Box>
    );
  },
);

TreatedClient.displayName = "TreatedClient";
export { TreatedClient };
